import { Edge } from '@xyflow/react';

import { MissionType } from '@/const/mission';
import { IConditionFlow, IMission, IScene, SceneType } from '@/interface';

import { nodeEnumMissionToType, NodeEnums, SCENE_SOURCE_NODE_ID } from '../const/node';
import { ConditionType } from '../nodes/condition/const';
import { templateNode } from '../nodes/templateNode';
import { AppNode } from '../types';
import { getConditionGroup } from './addNode';
import { confirmCondition, confirmMission, confirmScene } from './cofirm';
import { nodeCanDelete } from './disabled';
import { confirmGamePlay } from './gamePlay';
import { getNodeId } from './getNodeId';
import { getMissionDetailToMissionList } from './resInfoToNodeInfo';

export const getSceneBots = (scene: IScene) => scene?.botPromptList?.map((e) => {
  const { id = '', name = '未命名', avatar = {} } = e?.botDetail || {};
  return { id: e?.id || id, name, avatar };
}) || [];

export const getRelateBots = (scene: IScene) => scene?.extendMission?.relatedMessages?.
  map(e => e?.message?.sender?.senderId)
  .reverse() ?? [];

const exclSceneToNodes = (sceneDetail: IScene, nodes: AppNode[]) => {
  const missionList = sceneDetail?.missionList || [];

  const newNodes = nodes.flatMap((e, index) => {
    if (e.type === NodeEnums.startMission
      || e.type === NodeEnums.mainMission
      || e.type === NodeEnums.endMission) {
      return missionNode(e, missionList, index, '');
    }

    if (e.type === NodeEnums.addCondition) {
      // passCondition、trapCondition、successMission、failMission、jump同步在此处理
      return conditionGroupNode(e, index, sceneDetail);
    }

    if (e.type === NodeEnums.gamePlay) {
      return {
        ...e,
        id: getNodeId(index),
        data: {
          ...e.data,
          info: {
            ...confirmGamePlay(sceneDetail.id || '', sceneDetail.aiGamePlay),
          },
        },
      };
    }

    if (e.type === NodeEnums.scene) {
      const sceneInfo = { ...confirmScene({ ...sceneDetail }) };
      return {
        ...e,
        id: getNodeId(index),
        data: {
          ...e.data,
          info: {
            ...sceneInfo,
            isDraft: sceneInfo?.isDraft,
          },
        },
      };
    }

    return {
      ...e,
      id: getNodeId(index),
    };
  });

  const resultNodes: AppNode[] = (newNodes?.filter(e => e !== undefined)) as AppNode[] || [];
  const resultEdges: Edge[] = getEdges(resultNodes);
  return {
    addNodes: resultNodes,
    addEdges: resultEdges,
  };
};

// 生成连线
export const getEdges = (resultNodes: AppNode[]) => resultNodes?.map((e, i) => {
  let source = resultNodes[i - 1]?.id;
  const target = e?.id;

  if (e?.type === NodeEnums.scene) {
    source = SCENE_SOURCE_NODE_ID;
  }

  if (e?.type === NodeEnums.passCondition || e?.type === NodeEnums.trapCondition) {
    source = e?.data?.info?.rootId;
  }
  return {
    id: `e${source}-${target}`,
    source,
    target,
    type: 'smoothstep',
    deletable: false,
  };
});

// 剧情解析
export const exclMainList = (missionList: IScene['missionList']) => {
  const info = getMissionDetailToMissionList(missionList, MissionType.MISSION_TYPE_MAINLINE);

  const list: Partial<IMission>[] = info?.map((e) => {
    const mission = e?.mission || {};
    return {
      ...mission,
      canDelete: nodeCanDelete(mission),
    };
  });
  return list;
};


const missionNode = (e: AppNode, missionList: IScene['missionList'], index: number, missionId?: string) => {
  if (e.type === NodeEnums.startMission
    || e.type === NodeEnums.failMission
    || e.type === NodeEnums.successMission
    || e.type === NodeEnums.mainMission
    || e.type === NodeEnums.endMission
  ) {
    const missionType = nodeEnumMissionToType[e.type];
    if (e.type === NodeEnums.mainMission) {
      const list = exclMainList(missionList);
      return {
        ...e,
        id: getNodeId(index),
        data: {
          ...e.data,
          info: {
            list,
          },
        },
      };
    }
    if (e.type === NodeEnums.startMission || e.type === NodeEnums.endMission) {
      const info = getMissionDetailToMissionList(missionList, missionType);
      const otherInfo = info?.[0] || {};
      return {
        ...e,
        id: getNodeId(index),
        data: {
          ...e.data,
          info: {
            ...confirmMission(otherInfo?.mission),
          },
        },
      };
    }
    if (e.type === NodeEnums.failMission || e.type === NodeEnums.successMission) {
      const info = missionList?.find(item => item.mission?.id === missionId);
      if (!info) return false;
      return {
        ...e,
        id: getNodeId(index),
        data: {
          ...e.data,
          info: {
            ...confirmMission(info.mission),
          },
        },
      };
    }
  }
};

export const conditionGroupNode = (
  e: AppNode,
  index: number,
  scene: IScene,
) => {
  if (scene.isFinal) {
    return [
      {
        ...templateNode[NodeEnums.jump],
        id: getNodeId(index),
        data: {
          ...templateNode[NodeEnums.jump].data,
          info: {},
        },
      }];
  }

  const sceneId = scene.id || '';
  const rootId = getNodeId(index);
  const missionList = scene?.missionList || [];
  const missionMap = new Map<string, string>();
  missionList?.forEach((e) => {
    const mission = e.mission || {};
    missionMap.set(mission.id, mission.name);
  });

  const canDelete = nodeCanDelete(scene);
  const newNodes: AppNode[] = [];
  const conditionNodes: AppNode[] = [];
  const passConditions = scene.passConditions || [];
  passConditions.forEach((condition, index) => {
    const newCondition = confirmCondition(condition, index, sceneId, rootId, missionMap, canDelete, scene.sceneType);
    const { addNodes } = getConditionGroup(newCondition, e, scene.isFinal);
    if (!scene.isFinal) {
      // 修改过关和陷阱剧情
      const mId = newCondition.triggerMissionId;
      if (mId && newCondition.type === ConditionType.PASS) {
        const mNode = missionNode(addNodes[1], missionList, index, mId);
        if (mNode) addNodes[1] = mNode;
      }
    }
    conditionNodes.push(...addNodes);
  });

  if (scene.sceneType !== SceneType.SCENE_TYPE_OPTION) {
    // 兼容旧版本
    for (const type of [
      { condType: ConditionType.PASS, missionType: MissionType.MISSION_TYPE_SUCCESS },
      // { condType: ConditionType.TRAP, missionType: MissionType.MISSION_TYPE_FAILURE },
    ]) {
      const hasCond = passConditions.some(item => item.passCondType === type.condType);
      if (hasCond) continue;

      const nodes = missionToCondGroup(missionList, type, sceneId, rootId, e);
      if (nodes) conditionNodes.push(...nodes);
    }
  }

  // 过关条件为空时占位用
  const hasPass = passConditions.some(item => item.passCondType === ConditionType.PASS);
  if (!hasPass) {
    const newCondition: IConditionFlow = {
      id: '',
      conditionNodeId: '',
      sceneId,
      rootId,
      type: ConditionType.PASS,
      isEmpty: true,
      isDraft: false,
    };
    const { addNodes } = getConditionGroup(newCondition, e, scene.isFinal);
    conditionNodes.push(...addNodes);
  }

  // AddCondition节点
  const conditions = conditionNodes
    .filter(node => node.type === NodeEnums.passCondition
      || node.type === NodeEnums.trapCondition)
    .map(node => node.data.info);
  newNodes.push({
    ...e,
    id: rootId,
    data: {
      ...e.data,
      isEmptyNode: scene.sceneType === SceneType.SCENE_TYPE_OPTION,
      info: {
        ...e.data.info,
        id: rootId,
        sceneId,
        sceneType: scene.sceneType,
        isEmpty: true,
        conditions,
      },
    },
  });

  newNodes.push(...conditionNodes);
  return newNodes;
};

// 兼容旧版本，保证历史剧情默认关联条件
const missionToCondGroup = (
  missionList: IScene['missionList'],
  type: { condType: ConditionType, missionType: MissionType },
  sceneId: string,
  rootId: string,
  rootNode: AppNode,
) => {
  const filterList: IMission[] = [];
  missionList?.forEach((e) => {
    if (e.mission?.missionType === type.missionType) {
      filterList.push(e.mission);
    }
  });

  const allNodes: AppNode[] = [];
  filterList.forEach((mission, i) => {
    const mId = mission.id;
    const newCondition: IConditionFlow = {
      id: `condition_${getNodeId(i)}`,
      conditionNodeId: '',
      sceneId,
      rootId,
      triggerMissionId: mId,
      type: type.condType,
      isEmpty: false,
    };
    const { addNodes } = getConditionGroup(newCondition, rootNode);
    const mNode = missionNode(addNodes[1], missionList, i, mId);
    if (mNode) addNodes[1] = mNode;
    allNodes.push(...addNodes);
  });

  return allNodes;
};

export default exclSceneToNodes;
